import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import ProductHeader from './_product-header/component';
import ProductCarrousel from './_carrousel/component';
import OtherEditions from './_product-othereditions/component';
import PriceBlock from '../base_price-block/component';
import LoyaltyPrice from '@components/ui_loyalty-price/component';
import FloatingCTA from './_floating-cta/component';
import ProductPreview from './_preview/component';
import ProductDetails from './_bookdetails/component';
import ProductQuotes from './_quotes/component';
import PdpNaviMenu from './_navi-menu/component';
import Reviews from './_reviews/component';
import EBookNotice from './_ebook-notice/component';
import PdpTracklist from './_tracklist/component';
import QandA from './_q-and-a/component';
import { PromoNotification, Badge, Annotation, Excerpt } from 'audamatic-ui';
import Typeset from '@components/base_typeset/component';
import DataTricsContainer from '../sec_datatrics_container/component';
import { getDevice, isBrowser, isJsonString, getCookieValue } from '@utils';
import Restapi from '@utils/api';
import { Product } from '@utils/product';
import { productDetailGoogleTag } from '@utils/analytics.js';
import Text from '@text';
import { t } from '@utils/i18n';
import { AppContext } from '../../ssr/shared/application-context';
import GenericSection from '../base_generic-section/component';
import LogoutNotification from '@components/ui_logout-notification/component';
import store from '@utils/store';
import PrimaryActionsB2C from './_primary-actions-B2C/component';
import PrimaryActionsB2B from './_primary-actions-B2B/component';
import { IS_B2B } from '@config';
import Usps from './_usps/component';

import './styles/styles.scss';

if (isBrowser) {
  require('intersection-observer');
}

/**
 * PDP component
 * This is the complete decoupled pdp
 * Product description page, the product page
 */
class Pdp extends Component {
  constructor(props) {
    super(props);

    let product = props.product || null;
    const serverHasProduct = !!this.props.staticContext?.data?.[0];
    if (serverHasProduct) {
      product = this.props.staticContext?.data?.[0];
    } else if (isBrowser && window.__INITIAL_DATA__) {
      product = window.__INITIAL_DATA__[0];
    }

    this.state = {
      product,
      anchors: [],
      hasPdf: null,
      hasGooglePreview: null,
      googlePreviewUrl: '',
      reviewCount: null,
      floatingCTA: false,
      recentlyViewed: [],
    };

    this.pdfBaseUrl = '/images/active/InkijkPDF/cb/';

    this.restapi = new Restapi();
    this.btnContainerRef = createRef();
    this.descriptionRef = createRef();
  }

  componentDidMount() {
    if (isBrowser) {
      if (!this.state.product) {
        this.fetchProduct();
      } else {
        this.pollPdf();
        this.setObserver();
        // generate and push google tag for pdp
        this.pushGoogleTag();
        // Add the productdata to the recently viewed array in the appcontext
        this.context.updateRecentlyViewed(this.state.product);
        // Get the recently viewed product data to show in recently viewed slider
        this.getRecentlyViewedProductData();
      }
    }

    delete window.__INITIAL_DATA__;
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.match?.params?.path1 !== this.props.match?.params?.path1) {
      this.fetchProduct();
      /*
       * compare the recentlyviewed data from state with context.
       * If context was updated make sure the state is updated to always show up to date data on the pdp
       * CHECK IF THIS STILL WORKS WHEN API IS USED OTHERWISE REMOVE IT
       * (MIGHT CAUSE INVINITE LOOP IF DATA FROM API IS DIFFERENT THAN NORMAL PRODUCT DATA)
       */
    } else if (this.context.recentlyViewed !== this.state.recentlyViewed) {
      this.getRecentlyViewedProductData();
    }
  }

  setObserver = () => {
    const observer = new IntersectionObserver(this.floatingChecker, {
      rootMargin: '-107px 0px 0px 0px',
      threshold: this.thresholdArray(20),
    });
    observer.observe(this.btnContainerRef.current);
  };

  fetchProduct = () => {
    const { match = {}, fetchInitialData, updateProduct } = this.props;

    if (fetchInitialData) {
      Promise.all(fetchInitialData(match.params?.path1)).then((response) => {
        const [product] = response;
        updateProduct(product);
        this.setState({ product }, () => {
          this.pushGoogleTag();
          this.pollPdf();
          this.setObserver();
          // Add the productdata to the recently viewed array in the appcontext
          this.context.updateRecentlyViewed(product);
          // Get the recently viewed product data to show in recently viewed slider
          this.getRecentlyViewedProductData();
        });
      });
    }
  };

  /**
   * @param steps
   * @return {number[]}
   * Makes an array of the treshold steps used in the observer for the CTA
   */
  thresholdArray = (steps) =>
    Array(steps + 1)
      .fill(0)
      .map((_, index) => index / steps || 0);

  /**
   * @return {boolean}
   * checks if device is mobile and if floatingCTA should be activated
   */
  get shouldFloat() {
    return !!(this.isMobile && this.state.floatingCTA);
  }

  /**
   * @param entries = entries in observer callback
   * This callback checks if button should be floating or not and sets the state accordingly
   */
  floatingChecker = (entries) => {
    let previousY = 0;
    let previousRatio = 0;

    entries.forEach((entry) => {
      const currentY = entry.boundingClientRect.y;
      const currentRatio = entry.intersectionRatio;
      const { isIntersecting } = entry;
      let floating = false;

      // Scrolling down/up
      if (currentY < previousY) {
        floating = true;
      } else if (currentY > previousY && isIntersecting) {
        if (currentRatio > previousRatio) {
          floating = false;
        } else {
          floating = true;
        }
      }
      this.setState({
        floatingCTA: floating,
      });
      previousY = currentY;
      previousRatio = currentRatio;
    });
  };

  get apiKey() {
    if (DEVELOPMENT) {
      return this.store.apiKeys.books.dev;
    } else if (ACCEPTANCE) {
      return this.store.apiKeys.books.acc;
    } else if (PRODUCTION) {
      return this.store.apiKeys.books.prod;
    }
    return this.store.apiKeys.books.dev;
  }

  pollPdf = () => {
    const bookTypes = [
      'Paperback',
      'Dwarsligger',
      'Hardcover',
      'eBook',
      'Kartonboekje',
    ];
    if (!bookTypes.includes(this.product.type)) {
      return;
    }
    this.restapi
      .pollPdf(this.pdfUrl)
      .then(() => {
        this.setState({
          hasPdf: true,
        });
      })
      .catch(() => {
        this.setState(
          {
            hasPdf: false,
          },
          this.checkGooglePreview
        );
      });
  };

  checkGooglePreview() {
    this.restapi
      .pollGoogleBooksPreview(this.sku, this.apiKey)
      .then((response) => response.json())
      .then((data) => {
        if (data.totalItems > 0 && data.items[0].accessInfo.embeddable) {
          this.setState({
            hasGooglePreview: true,
            googlePreviewUrl: data.items[0].volumeInfo.previewLink,
          });
        } else {
          this.hidePreviewPanel();
        }
      });
  }

  /**
   * Pushes the google tag for pdp when component is mounted if the
   * script with type data-productid is available in the source.
   */
  pushGoogleTag() {
    this.store.pushToDataLayer(
      productDetailGoogleTag({
        ...this.product,
        ...this.additionalProductDetails,
      })
    );
  }

  /**
   * This is for analytics
   */
  get additionalProductDetails() {
    const scripts = document.querySelectorAll(
      `script[type='application/ld+json']`
    );
    let bookData = {};
    [...scripts].forEach((script) => {
      const json = isJsonString(script.innerHTML)
        ? JSON.parse(script.innerHTML)
        : {};
      if (json['@type'] === 'Book') {
        bookData = {
          language: json.inLanguage,
          keywords: json.keywords,
          pages: json.numberOfPages,
          imageLength: json.image && json.image.length, // @todo: should be refactored to the images from the generic product JSON, but now it only supports two images, Thumbnail and Image
        };
      }
    });
    return bookData;
  }

  hidePreviewPanel = () => {
    this.previewAnchor && this.previewAnchor.setAttribute('hidden', true);
  };

  get store() {
    return this.props.store;
  }

  setReviewCount = (reviewCount) => {
    this.setState({ reviewCount });
  };

  /**
   * The anchors that are needed for the navbar
   * @param elm
   */
  setDomAnchor = (elm) => {
    if (elm) {
      this.setState((prevState) => ({ anchors: [...prevState.anchors, elm] }));
    }
  };

  get product() {
    return new Product(this.state.product || {});
  }

  get sku() {
    return this.product.sku || this.product.SKU;
  }

  get badges() {
    return this.product.badges || [];
  }

  get previewAnchor() {
    return this.state.anchors.find((elm) => elm.id === 'preview');
  }

  get pdfUrl() {
    if (!!this.product.viewingcopy) {
      return this.product.viewingcopy;
    }
    return `${this.pdfBaseUrl}${this.sku}.pdf`;
  }

  handlePdfFetchError = (error) => {
    if (typeof this.errorFn === 'function') {
      this.errorFn(error);
    }
    return Promise.reject(error);
  };

  fetchPreviewPdf = (pdfEngine) => {
    return pdfEngine
      .load(this.pdfUrl, this.handlePdfFetchError)
      .catch(this.handlePdfFetchError);
  };

  get productPreviewComponent() {
    const { hasPdf, hasGooglePreview, googlePreviewUrl } = this.state;
    const previewVisibilityClass =
      hasPdf || hasGooglePreview ? 'visible' : 'hidden';

    if ((hasPdf || hasGooglePreview) !== false) {
      return (
        <div className={`preview-container ${previewVisibilityClass}`}>
          <ProductPreview
            store={this.store}
            product={this.product}
            pdfUrl={this.pdfUrl}
            googlePreviewUrl={googlePreviewUrl}
            fetchPdf={this.fetchPreviewPdf}
            hasPdf={hasPdf}
            hasGooglePreview={hasGooglePreview}
          />
        </div>
      );
    }
    return null;
  }

  get isMobile() {
    return !!getDevice().isMobile;
  }

  get isBook() {
    const { product } = this.state;
    const cats = ['Boeken', 'Engelse Boeken', 'eBooks'];
    return !!cats.includes(product?.catalog);
  }

  get prices() {
    const { price1, price2 } = this.product.getProductPrice();
    return { price1, price2 };
  }
  get regularPrices() {
    return this.product.price.regular || {};
  }

  descriptionOverflow = () => {
    if (this.descriptionRef.current) {
      const { clientHeight, scrollHeight } = this.descriptionRef.current;
      return scrollHeight > clientHeight;
    }
    return false;
  };

  /*
   * Placeholder for api call to get productdata by sku
   * getProductBySkus does not exist yet, needs to be created then
   */
  getProductsBySkus = (skus) => {
    // returns data from api call to get product by skus with array of skus as parameter
    if (skus.length > 0) {
      // return this.restapi.getProductsBySkus(skus); // uncommend when api call can be made
    }
    return [];
  };

  getRecentlyViewedProductData = async () => {
    /*
     * Get recently viewed skus from cookie
     * Only send the skus to api that we want to show. Do not include sku from current pdp.
     */
    const skus = getCookieValue('recentlyViewed');
    const skusWithoutCurrentProduct = skus.slice(0, skus.length - 1);

    // const productData = await this.getProductsBySkus(skusWithoutCurrentProduct); // uncommend this
    console.log('skus', skusWithoutCurrentProduct); // remove this console log when api call is used

    this.setState({ recentlyViewed: this.context.recentlyViewed }); // change to productData from api when possible
  };

  render() {
    const { product, anchors = [], recentlyViewed } = this.state;

    if (!product) {
      return null; // TODO: return pdp skeleton
    }

    /*
     * Recently viewed products:
     * Show max of 4 products
     * Switch the order to always show the last visited pdp as the first product in slider
     */
    const lastIndex = recentlyViewed.length - 1; // Remove when api data is used > current product is not in response api
    const slicedRecentlyViewed = recentlyViewed.slice(0, lastIndex); // Remove when api data is used > current product is not in response api
    const flippedRecentlyViewed = slicedRecentlyViewed.reverse(); // Change to recentlyViewed state when api data is used

    return (
      <>
        {anchors.length > 0 && (
          <PdpNaviMenu
            anchorDomElements={anchors}
            cta={undefined}
            store={this.store}
            product={this.product}
          />
        )}
        <div className="container product-detail-wrapper cart-interaction-container">
          <LogoutNotification spacing={8} />
          <div className="product-detail-info clearfix">
            <div className="product-carrousel">
              <span
                title="Datatrics anchor for Product image badge"
                id="datatrics-pdp-badge"
              >
                {!!this.product.badges?.length && (
                  <Badge
                    variant="tag"
                    text={<Text>comp.linebadge.promotion</Text>}
                  />
                )}
              </span>
              <ProductCarrousel
                bookFlip={this.product.getBookFlip()}
                productWidth={this.product.productWidth}
                productHeight={this.product.productHeight}
                imageShadow={this.product.imageshadow}
                frontImage={this.product.frontImage}
                backImage={this.product.images.backImage}
                productType={this.product.tbaproducttype}
                inlayImage={this.product.images.inlayImage}
                store={this.store}
                previewComponent={this.productPreviewComponent}
                alt={this.product.name}
              />
            </div>
            <ProductHeader
              store={this.store}
              product={this.product}
              reviewCount={this.state.reviewCount}
            />
            <div className="product-detail-content">
              {!this.product.isGiftcard && (
                <OtherEditions
                  data={this.product.otherEditionsJson}
                  store={this.store}
                />
              )}

              <div className="cart-interaction-container">
                {this.product.isOnline && (
                  <div className="product-detail-price-wrapper">
                    {this.product.isDeLoyaltyProduct ? (
                      <LoyaltyPrice size="large" prices={this.regularPrices} />
                    ) : (
                      <PriceBlock
                        size="large"
                        product={this.product}
                        store={this.store}
                      />
                    )}
                    <span
                      title="Datatrics anchor for Price block"
                      id="datatrics-pdp-price"
                    />
                    {this.isBook && this.prices.price1 >= 20 && (
                      <Typeset tag="div" bold color="cta">
                        <Text>pdp.product.free-shipping</Text>
                      </Typeset>
                    )}
                  </div>
                )}
              </div>

              {!!this.product.badges?.length && (
                <div className="promo-placement">
                  <PromoNotification
                    size="medium"
                    title={this.badges[0].title}
                    subtitle={this.badges[0].subtitle}
                    link={this.badges[0].link}
                  />
                </div>
              )}

              {this.product.isEbook && (
                <EBookNotice product={this.product} store={this.store} />
              )}

              {IS_B2B ? (
                <PrimaryActionsB2B store={this.store} product={this.product} />
              ) : (
                <PrimaryActionsB2C store={this.store} product={this.product} />
              )}

              <FloatingCTA
                shouldFloat={this.shouldFloat}
                prices={this.prices}
                store={this.store}
                product={this.product}
              />

              <Usps store={this.store} product={this.product} />

              <span
                ref={this.btnContainerRef}
                className="invisible-float-trigger"
              />

              {!this.product.isOnline && (
                <p>
                  <Text>pdp.product.not-available</Text>
                </p>
              )}
              <div className="description">
                <span
                  title="Datatrics anchor for Promotion"
                  id="datatrics-pdp-promotion"
                />

                {this.product.annotation && (
                  <div className="annotation-wrap">
                    <Annotation annotation={this.product.annotation} />
                  </div>
                )}
                {this.product.longDescription && (
                  <>
                    {t('pdp.description.title') && !!this.isBook && (
                      <h2>
                        <Text>pdp.description.title</Text>
                      </h2>
                    )}
                    <Excerpt
                      text={this.product.longDescription}
                      maxLines={8}
                      label1={<Text>common.link.readmore</Text>}
                      label2={<Text>common.link.readless</Text>}
                    />
                  </>
                )}

                {this.product.termsAndConditions && (
                  <div className="terms-and-conditions">
                    <h2>
                      <Text>pdp.terms</Text>
                    </h2>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: this.product.termsAndConditions,
                      }}
                    />
                  </div>
                )}
                {this.isBook && <QandA product={this.product} />}
              </div>
            </div>
          </div>
        </div>

        <DataTricsContainer />

        <div className="container-fluid pdp-sections">
          <div className="row">
            {Object.entries(this.product.mediaQuotesRatings).length !== 0 && (
              <>
                <a
                  ref={this.setDomAnchor}
                  className="hidden-xs"
                  id="quotes"
                  title={t('pdp.menu.panel.pressquotes')}
                  data-icon="ico-media-coverage"
                  data-nav="#pdp-menu"
                />
                <ProductQuotes quotes={this.product.mediaQuotesRatings} />
              </>
            )}
            <a
              ref={this.setDomAnchor}
              className="hidden-xs"
              id="reviews"
              title={t('pdp.menu.panel.reviews')}
              data-icon="ico-review"
              data-nav="#pdp-menu"
            />
            <Reviews
              setReviewCount={this.setReviewCount}
              productCatalog={this.product.catalog}
              sku={this.product.sku}
              rating={this.product.rating}
              store={this.store}
              productName={this.product.name}
            />
            {!!this.product.trackData.length && (
              <>
                <a
                  ref={this.setDomAnchor}
                  className="hidden-xs"
                  id="tracklist"
                  title={t('pdp.menu.panel.tracklist')}
                  data-icon="ico-music"
                  data-nav="#pdp-menu"
                />
                <PdpTracklist
                  trackData={this.product.trackData}
                  type={this.product.type}
                  person={
                    this.product.persons.length
                      ? this.product.persons[0].name
                      : ''
                  }
                />
              </>
            )}
            <a
              ref={this.setDomAnchor}
              className="hidden-xs"
              id="details"
              title={t('pdp.menu.panel.details')}
              data-icon="ico-details"
              data-nav="#pdp-menu"
            />
            <ProductDetails product={this.product} />
            {recentlyViewed.length > 1 && ( // change to > 0 when api data is used (current product is not in there then)
              <div className="container">
                <GenericSection
                  store={store}
                  layout="one-component"
                  title={<Text>recently.viewed</Text>}
                  components={[
                    {
                      maxItems: '4',
                      'component-type': 'ankilerpromos',
                      products: flippedRecentlyViewed,
                    },
                  ]}
                />
              </div>
            )}
          </div>
        </div>
      </>
    );
  }
}

Pdp.contextType = AppContext;

Pdp.propTypes = {
  store: PropTypes.object,
  product: PropTypes.object,
  fetchInitialData: PropTypes.func,
  match: PropTypes.object,
  staticContext: PropTypes.object,
  updateProduct: PropTypes.func,
};

export default Pdp;
